/**
 * FilesViewer Component
 *
 * Displays files attached to workflows and individual nodes.
 * Features:
 * - Collapsible panel for workflow-level files
 * - Node-specific file filtering
 * - File download links
 * - File type icons and metadata
 */

import React, { useState, useMemo } from 'react';
import {
  FileText,
  FileImage,
  FileVideo,
  File as FileIcon,
  Download,
  Paperclip,
  ChevronDown,
  ChevronRight,
  X,
  Tag,
  Eye
} from 'lucide-react';
import { getWorkflowIdentifier } from '../utils/workflowIdentifier';
import { openFileInNewTab, internalFileIdFromUrl } from '../api/filesApi';

/**
 * Get appropriate icon for file type
 */
function getFileIcon(mimeType) {
  if (!mimeType) return <FileIcon size={16} />;

  if (mimeType.startsWith('image/')) return <FileImage size={16} />;
  if (mimeType.startsWith('video/')) return <FileVideo size={16} />;
  if (mimeType.startsWith('text/') || mimeType.includes('pdf')) return <FileText size={16} />;

  return <FileIcon size={16} />;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes) {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Individual file item component
 */
function FileItem({ file, workflowIdentifier, workflowId, onDownload }) {
  const handleDownload = () => {
    if (onDownload) {
      onDownload(file);
    } else {
      const id = file.file_id || file.id || internalFileIdFromUrl(file.download_url);
      // No auth cookie any more: get a signed 5-min URL with the Bearer first.
      if (id) {
        openFileInNewTab(id).catch((err) => {
          console.error('Open file failed:', err);
        });
      }
    }
  };

  const handleViewWorkflow = () => {
    if (workflowId) {
      window.location.href = `/app/workflow/${workflowId}`;
    }
  };

  // Check if filename contains workflow identifier
  const hasWorkflowId = file.filename?.includes(workflowIdentifier);

  return (
    <div className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="text-gray-600 flex-shrink-0">
          {getFileIcon(file.mime_type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {file.filename}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="text-xs text-gray-500">
              {formatFileSize(file.file_size)}
            </div>
            {hasWorkflowId && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 border border-blue-200 rounded text-xs text-blue-700">
                <Tag size={10} />
                <span className="font-mono">{workflowIdentifier}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {workflowId && (
          <button
            onClick={handleViewWorkflow}
            className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
            title="View workflow"
          >
            <Eye size={16} />
          </button>
        )}
        <button
          onClick={handleDownload}
          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
          title="Download file"
        >
          <Download size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * Node files section - shows files attached to specific node
 */
function NodeFilesSection({ node, workflowIdentifier, workflowId, expanded, onToggle }) {
  const files = node.data?.attachedFiles || [];

  if (files.length === 0) return null;

  return (
    <div className="border-t border-gray-200 pt-2">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left p-2 hover:bg-gray-50 rounded transition-colors"
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Paperclip size={14} className="text-gray-600" />
        <span className="text-sm font-medium text-gray-700">
          {node.data?.displayName || 'Node'} - {files.length} file{files.length > 1 ? 's' : ''}
        </span>
      </button>

      {expanded && (
        <div className="ml-6 mt-2 space-y-2">
          {files.map((file, index) => (
            <FileItem
              key={file.file_id || index}
              file={file}
              workflowIdentifier={workflowIdentifier}
              workflowId={workflowId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Main FilesViewer component
 */
export default function FilesViewer({ workflow, nodes, isOpen, onClose }) {
  const [expandedWorkflowFiles, setExpandedWorkflowFiles] = useState(true);
  const [expandedNodeIds, setExpandedNodeIds] = useState(new Set());

  // Get workflow identifier and ID
  const workflowIdentifier = useMemo(() => {
    return getWorkflowIdentifier(workflow);
  }, [workflow]);

  const workflowId = workflow?.id;

  // Collect all workflow-level files
  const workflowFiles = useMemo(() => {
    return workflow?.attached_files || workflow?.attachedFiles || [];
  }, [workflow]);

  // Get nodes with files
  const nodesWithFiles = useMemo(() => {
    if (!nodes) return [];
    return nodes.filter(node => {
      const files = node.data?.attachedFiles || [];
      return files.length > 0;
    });
  }, [nodes]);

  // Calculate total file count
  const totalFiles = useMemo(() => {
    const workflowCount = workflowFiles.length;
    const nodeCount = nodesWithFiles.reduce((sum, node) => {
      return sum + (node.data?.attachedFiles?.length || 0);
    }, 0);
    return workflowCount + nodeCount;
  }, [workflowFiles, nodesWithFiles]);

  const toggleNodeFiles = (nodeId) => {
    setExpandedNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-96 bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Paperclip size={20} className="text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">
              Attached Files
            </h3>
            {totalFiles > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                {totalFiles}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Tag size={12} />
            <span className="font-mono">{workflowIdentifier}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {totalFiles === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Paperclip size={48} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No files attached to this workflow</p>
          </div>
        ) : (
          <>
            {/* Workflow-level files */}
            {workflowFiles.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <button
                  onClick={() => setExpandedWorkflowFiles(!expandedWorkflowFiles)}
                  className="flex items-center gap-2 w-full text-left mb-2"
                >
                  {expandedWorkflowFiles ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="text-sm font-semibold text-blue-900">
                    Workflow Files ({workflowFiles.length})
                  </span>
                </button>

                {expandedWorkflowFiles && (
                  <div className="space-y-2 mt-2">
                    {workflowFiles.map((file, index) => (
                      <FileItem
                        key={file.file_id || index}
                        file={file}
                        workflowIdentifier={workflowIdentifier}
                        workflowId={workflowId}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Node-level files */}
            {nodesWithFiles.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Files by Node ({nodesWithFiles.length} nodes)
                </h4>
                <div className="space-y-1">
                  {nodesWithFiles.map(node => (
                    <NodeFilesSection
                      key={node.id}
                      node={node}
                      workflowIdentifier={workflowIdentifier}
                      workflowId={workflowId}
                      expanded={expandedNodeIds.has(node.id)}
                      onToggle={() => toggleNodeFiles(node.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
